import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import cookie from "react-cookies";
import { useNavigate } from "react-router-dom";
import { Icons } from "../constants/icon-library";
import { clearUser } from "../features/user/userSlice";
import { getMenuItems, ROLE } from "../constants/constants";
import { upsertSetting } from "../features/settings/settingsSlice";
import {
  getAvatarColor,
  getInitials,
} from "../platforms/Meetings/Meetings.utils";
import { switchOrganization } from "../features/user/userSlice";
import { saveAuthToken } from "../utils/authHeader";
import { setUser } from "../features/user/userSlice";
import { toast } from "react-toastify";

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user, organizations } = useSelector((state) => state.user);
  const { balance } = useSelector((state) => state.transactions);

  const [theme, setTheme] = useState(
    localStorage.getItem("phoenixTheme") || "light",
  );

  useEffect(() => {
    const currentTheme = localStorage.getItem("phoenixTheme") || "light";
    document.body.classList.toggle("dark", currentTheme === "dark");
    const toggle = document.getElementById("themeControlToggle");
    if (toggle) {
      toggle.checked = currentTheme === "dark";
      toggle.addEventListener("change", () => {
        const newTheme = toggle.checked ? "dark" : "light";
        localStorage.setItem("phoenixTheme", newTheme);
        document.body.classList.toggle("dark", newTheme === "dark");
        setTheme(newTheme);
      });
    }
  }, []);

  const handleLogout = () => {
    cookie.remove("token", { path: "/" });
    dispatch(clearUser());
    dispatch(upsertSetting());
    navigate("/sign-in", { replace: true });
  };

  const handleOrganizationChange = async (organizationUuid) => {
    try {
      const response = await dispatch(
        switchOrganization({
          orgUuid: organizationUuid,
        }),
      ).unwrap();

      const { token, ...userData } = response;
      cookie.remove("token", { path: "/" });
      dispatch(clearUser());

      if (token) {
        saveAuthToken(token);
      }

      dispatch(setUser({ ...userData, role: response.role }));

      toast.success("Switch organization successful");
      navigate("/", { replace: true });
    } catch (err) {
      let message =
        err ||
        err?.response?.message ||
        err?.response?.data?.message ||
        "Organization switch failed.";

      toast.error(message);
    }
  };
  const displayName = useMemo(() => {
    if (user?.name?.trim()) return user.name;
    return "User";
  }, [user?.name]);

  const avatarColor = useMemo(() => getAvatarColor(displayName), [displayName]);
  const initials = useMemo(() => getInitials(displayName), [displayName]);
  const role = user?.role ?? null;

  const menuItems = getMenuItems(navigate);

  const hasOrganizations = Boolean(organizations?.length > 1);
  const currentOrgUuid = user?.org;

  const Avatar = ({ size = 36, fontSize = 13 }) => (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0"
      style={{
        width: size,
        height: size,
        fontSize,
        backgroundColor: avatarColor,
        letterSpacing: 0.5,
      }}
    >
      {initials}
    </div>
  );

  return (
    <nav
      className="navbar navbar-top fixed-top navbar-expand"
      id="navbarDefault"
    >
      <div className="collapse navbar-collapse justify-content-between">
        <div className="navbar-logo">
          <button
            className="btn navbar-toggler navbar-toggler-humburger-icon hover-bg-transparent"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarVerticalCollapse"
            aria-controls="navbarVerticalCollapse"
            aria-expanded="false"
            aria-label="Toggle Navigation"
          >
            <span className="navbar-toggle-icon">
              <span className="toggle-line"></span>
            </span>
          </button>
          <a className="navbar-brand me-1 me-sm-3" href="/">
            <div className="d-flex align-items-center">
              <img
                src={
                  theme === "dark"
                    ? "/assets/img/icons/logo.png"
                    : "/assets/img/icons/logo-dark.png"
                }
                alt="steertask"
                width={100}
              />
            </div>
          </a>
        </div>

        <ul className="navbar-nav navbar-nav-icons flex-row align-items-center gap-2">
          <li className="nav-item">
            <div className="theme-control-toggle px-2">
              <input
                className="form-check-input ms-0 theme-control-toggle-input"
                type="checkbox"
                data-theme-control="phoenixTheme"
                value="dark"
                id="themeControlToggle"
              />
              <label
                className="mb-0 theme-control-toggle-label theme-control-toggle-light"
                htmlFor="themeControlToggle"
                style={{ height: "32px", width: "32px" }}
              >
                <i className={`wb ${Icons("moon")} mt-1 align-middle`} />
              </label>
              <label
                className="mb-0 theme-control-toggle-label theme-control-toggle-dark"
                htmlFor="themeControlToggle"
                style={{ height: "32px", width: "32px" }}
              >
                <i className={`wb ${Icons("sun")} mt-1 align-middle`} />
              </label>
            </div>
          </li>
          {role === ROLE.USER && (
            <li
              className="nav-item"
              onClick={() => navigate("/billing")}
              style={{ cursor: "pointer" }}
            >
              <div
                className="d-flex align-items-center gap-2 px-3 py-1"
                style={{ fontSize: 18 }}
              >
                <div>
                  <div className="fw-semibold lh-1">{balance?.netBalance}</div>
                  <div className="text-body-secondary" style={{ fontSize: 10 }}>
                    Available Credits
                  </div>
                </div>
              </div>
            </li>
          )}

          <li className="nav-item dropdown">
            <a
              className="nav-link lh-1 pe-0 text-body d-flex align-items-center gap-2"
              id="navbarDropdownUser"
              href="#!"
              role="button"
              data-bs-toggle="dropdown"
              data-bs-auto-close="true"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <Avatar size={36} fontSize={13} />
              <div className="d-flex flex-column justify-content-center d-none d-sm-flex">
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {displayName}
                </span>
              </div>
              <i
                className="bi bi-chevron-down text-body-secondary"
                style={{ fontSize: 10 }}
              />
            </a>

            <div
              className="dropdown-menu dropdown-menu-start navbar-dropdown-caret py-0 dropdown-profile shadow border"
              aria-labelledby="navbarDropdownUser"
              style={{ minWidth: 240, position: "absolute" }}
            >
              {hasOrganizations ? (
                <div
                  className="d-flex"
                  style={{ width: 400, maxWidth: "80vw" }}
                >
                  <div
                    className="d-flex flex-column border-end flex-shrink-0"
                    style={{ width: 190 }}
                  >
                    <div
                      className="text-uppercase text-body-tertiary fw-bold px-3 pt-3 pb-1"
                      style={{ fontSize: 10, letterSpacing: "0.06em" }}
                    >
                      Switch Account
                    </div>

                    <div
                      className="flex-grow-1 py-1 px-1 overflow-auto"
                      style={{ maxHeight: 320 }}
                    >
                      {organizations.map((organization) => {
                        const isActive = organization.uuid === currentOrgUuid;
                        return (
                          <button
                            key={organization.uuid}
                            className="btn border-0 w-100 d-flex align-items-center justify-content-between gap-2 py-2 px-2 profile-menu-item text-truncate"
                            style={{ fontSize: 13 }}
                            onClick={() =>
                              handleOrganizationChange(organization.uuid)
                            }
                          >
                            <span className="text-truncate">
                              {organization.name}
                            </span>
                            {isActive && (
                              <i
                                className="bi bi-check-lg text-primary flex-shrink-0"
                                style={{ fontSize: 14 }}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <hr className="my-1 mx-2" />

                    <div className="py-2 px-1">
                      <button
                        className="btn border-0 w-100 text-start d-flex align-items-center gap-2 px-2 py-2 profile-menu-item text-danger"
                        style={{ fontSize: 13 }}
                        onClick={handleLogout}
                      >
                        <i
                          className={`wb ${Icons("logoutUser")} fs-8 align-middle`}
                          style={{ width: 16 }}
                        />
                        <span>Sign out</span>
                      </button>
                    </div>
                  </div>

                  <div className="card position-relative border-0 flex-grow-1">
                    <div className="card-body pb-2 pt-3 px-3">
                      <div className="d-flex align-items-center gap-3">
                        <Avatar size={48} fontSize={18} />
                        <div>
                          <div
                            className="fw-semibold text-body-emphasis"
                            style={{ fontSize: 14 }}
                          >
                            {displayName}
                          </div>
                          {user?.email && (
                            <div
                              className="text-body-secondary"
                              style={{ fontSize: 11 }}
                            >
                              {user.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <hr className="my-1 mx-3" />

                    <div className="card-body pt-1 pb-3 px-2">
                      {menuItems.map(({ label, icon, onClick }) => (
                        <button
                          key={label}
                          className="btn border-0 w-100 text-start d-flex align-items-center gap-2 px-3 py-2 profile-menu-item"
                          style={{ fontSize: 13 }}
                          onClick={onClick}
                        >
                          <i
                            className={`wb ${Icons(icon)} fs-8 text-body-secondary align-middle`}
                            style={{ width: 16 }}
                          />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="card position-relative border-0"
                  style={{ minWidth: 240 }}
                >
                  <div className="card-body pb-2 pt-3 px-3">
                    <div className="d-flex align-items-center gap-3">
                      <Avatar size={48} fontSize={18} />
                      <div>
                        <div
                          className="fw-semibold text-body-emphasis"
                          style={{ fontSize: 14 }}
                        >
                          {displayName}
                        </div>
                        {user?.email && (
                          <div
                            className="text-body-secondary"
                            style={{ fontSize: 11 }}
                          >
                            {user.email}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <hr className="my-1 mx-3" />

                  <div className="card-body pt-1 pb-2 px-2">
                    {menuItems.map(({ label, icon, onClick }) => (
                      <button
                        key={label}
                        className="btn border-0 w-100 text-start d-flex align-items-center gap-2 px-3 py-2 profile-menu-item"
                        style={{ fontSize: 13 }}
                        onClick={onClick}
                      >
                        <i
                          className={`wb ${Icons(icon)} fs-8 text-body-secondary align-middle`}
                          style={{ width: 16 }}
                        />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>

                  <hr className="my-1 mx-3" />

                  <div className="card-footer border-0 pt-1 pb-3 px-2">
                    <button
                      className="btn border-0 w-100 text-start d-flex align-items-center gap-2 px-3 py-2 profile-menu-item text-danger"
                      style={{ fontSize: 13 }}
                      onClick={handleLogout}
                    >
                      <i
                        className={`wb ${Icons("logoutUser")} fs-8 align-middle`}
                        style={{ width: 16 }}
                      />
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Header;
